import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2, User, Heart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@tabipla/ui/components/dialog";
import { useBookletLikes } from "../../../application/hooks/useDiscover";
import type { BookletLikeUserDto } from "@tabipla/shared";

interface LikeUsersModalProps {
  bookletId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LikeUsersModal({ bookletId, open, onOpenChange }: LikeUsersModalProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useBookletLikes(open ? bookletId : undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500" />
            {t("booklets.likedBy", "いいねしたユーザー")}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : data?.users && data.users.length > 0 ? (
            <div className="divide-y">
              {data.users.map((user: BookletLikeUserDto) => (
                <Link
                  key={user.id}
                  to={`/users/${user.id}`}
                  onClick={() => onOpenChange(false)}
                  className="flex items-center gap-3 px-1 py-3 transition-colors hover:bg-muted/50"
                >
                  {user.image ? (
                    <img
                      src={user.image}
                      alt={user.name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : user.avatarEmoji && user.avatarColor ? (
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br ${user.avatarColor}`}
                    >
                      {user.avatarEmoji}
                    </div>
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(user.likedAt).toLocaleDateString("ja-JP")}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("booklets.noLikes", "まだいいねがありません")}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
